/**
 * Login Page
 * 
 * Per-tenant password authentication with early demo disclaimer.
 */

import { useState } from "react";
import { useAuth } from "../lib/auth-context";

export function LoginPage() {
  const { login, isLoading: authLoading } = useAuth();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;

    setIsSubmitting(true);
    setError("");

    const result = await login(password);
    
    if (!result.success) {
      setError(result.error || "Authentication failed");
    }
    
    setIsSubmitting(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#FAFAFA" }}>
        <div 
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" 
          style={{ borderColor: "#00FBA9", borderTopColor: "transparent" }} 
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#FAFAFA" }}>
      <div className="w-full max-w-md px-8">
        {/* Logo */}
        <div className="text-center mb-6">
          <img 
            src="/images/pentatonic-logo.svg" 
            alt="Pentatonic" 
            className="h-8 mx-auto mb-4"
            style={{ filter: 'brightness(0)' }}
          />
        </div>

        {/* Disclaimer Banner */}
        <div 
          className="mb-6 p-4 rounded-lg border"
          style={{ 
            backgroundColor: "rgba(0, 251, 169, 0.08)", 
            borderColor: "rgba(0, 251, 169, 0.3)" 
          }}
        >
          <div className="flex items-start gap-3">
            <div 
              className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ backgroundColor: "#00FBA9" }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#171717" strokeWidth="2.5">
                <path d="M12 16v-4M12 8h.01" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium mb-1" style={{ color: "#171717" }}>
                Early Demo Notice
              </p>
              <p className="text-xs leading-relaxed" style={{ color: "#525252" }}>
                This is an early-stage demonstration designed to showcase Pentatonic's technology capabilities. 
                It is not intended as a reflection of final business requirements or production functionality, 
                but rather as a conversation starter to explore possibilities together.
              </p>
            </div>
          </div>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm mb-2" style={{ color: "#525252" }}>
              Access Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your access password"
              className="w-full px-4 py-3 border rounded-lg text-base focus:outline-none focus:ring-2 transition-all"
              style={{ 
                borderColor: error ? "#FF4C00" : "rgba(169,169,169,0.3)",
                backgroundColor: "#FFFFFF",
                color: "#171717",
              }}
              autoFocus
              disabled={isSubmitting}
            />
            {error && (
              <p className="text-sm mt-2" style={{ color: "#FF4C00" }}>{error}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !password.trim()}
            className="w-full py-3 rounded-lg text-base font-medium transition-all disabled:opacity-50"
            style={{ 
              backgroundColor: "#00FBA9",
              color: "#171717",
            }}
          >
            {isSubmitting ? "Authenticating..." : "Access Platform"}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs" style={{ color: "#A9A9A9" }}>
            Contact your Pentatonic representative if you need access credentials.
          </p>
        </div>

        {/* Pentatonic branding */}
        <div className="mt-12 pt-6 border-t text-center" style={{ borderColor: "rgba(169,169,169,0.2)" }}>
          <p className="text-xs" style={{ color: "#A9A9A9" }}>
            © {new Date().getFullYear()} Pentatonic. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}

